import type { Cell } from '@tabel/shared/schemas/cells';
import type { Timesheet } from '@tabel/shared/schemas/timesheets';
import { TriangleAlert, Users } from 'lucide-react';
import { useEffect, useRef } from 'react';
import {
  STATE_LABEL,
  STATE_SHORT,
  brigadeList,
  dayOffLabel,
  engineNote,
  hours,
  shownHours,
  weekday,
} from '../lib/format';
import { RECALC_LABEL, RECALC_TEXT, staleEditOf } from '../lib/recalc';
import { SIDE_PANEL_ROOM, useSidePanelOpen } from './ui';

/** «По СКУД» в подсказке: часы, иначе состояние дня. */
function machineText(c: Cell): string {
  if (c.machineHours != null) return `${shownHours(c.machineValue, c.machineHours)} ч`;
  const state = STATE_LABEL[c.machineState] || '—';
  if (c.errorCode) return `${state} (${c.errorCode})`;
  // Табель на движке (`machineValue` — `null`; в 001 поля нет): в день с кодом отсутствия присутствие ушло в документ,
  // «По СКУД» — «—», а не состояние проходов (mvp.md, «Решения по ходу», C3).
  if (c.absenceCode && c.machineValue === null) return '—';
  return state;
}

/** Ключ ячейки в разметке (`data-cell`): по нему страница возвращает фокус на ячейку после закрытия панели. */
export function cellKey(personId: string, date: string): string {
  return `${personId}|${date}`;
}

/** Кнопка ячейки по ключу. */
export function findCell(key: string, root: ParentNode = document): HTMLElement | null {
  for (const el of root.querySelectorAll<HTMLElement>('[data-cell]')) if (el.dataset.cell === key) return el;
  return null;
}

/**
 * Сколько правой части таблицы закрывает правая панель, px: от 901 px панель лежит поверх листа у правого края; до
 * 900 px она на весь экран — таблицу не сужает (её нет в режиме `side`).
 */
function coveredByPanel(wrap: HTMLElement): number {
  const panel = document.querySelector<HTMLElement>('[role="dialog"][data-mode="side"]');
  if (!panel) return 0;
  const covered = wrap.getBoundingClientRect().right - panel.getBoundingClientRect().left;
  return Math.min(wrap.clientWidth, Math.max(0, covered));
}

/**
 * Прокрутить таблицу к ячейке: по горизонтали — с учётом прилипшей колонки ФИО и правой панели поверх листа (T107), по
 * вертикали — если ячейка вне экрана (как в прототипе). Ячейка с отступами 24 px не помещается между колонкой ФИО и
 * панелью — она посередине этого места: повтор после появления панели не перекидывает таблицу от края к краю.
 */
function scrollToCell(wrap: HTMLDivElement | null, key: string) {
  const b = wrap ? findCell(key, wrap) : null;
  const td = b?.parentElement;
  if (!wrap || !b || !td) return;
  const name = wrap.querySelector<HTMLElement>('th')?.offsetWidth ?? 0;
  const visible = wrap.clientWidth - coveredByPanel(wrap);
  const left = td.offsetLeft - name - 24;
  const right = td.offsetLeft + td.offsetWidth - visible + 24;
  if (right > left)
    wrap.scrollLeft = Math.max(0, td.offsetLeft - name - (visible - name - td.offsetWidth) / 2);
  else if (wrap.scrollLeft > left) wrap.scrollLeft = Math.max(0, left);
  else if (wrap.scrollLeft < right) wrap.scrollLeft = right;
  const r = b.getBoundingClientRect();
  if (r.top < 64 || r.bottom > window.innerHeight - 16)
    b.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
}

/** Значок дня в двух бригадах (Q-15). */
const MULTI_BRIGADE = 'две бригады в один день';
/** Значок предупреждений движка за день (потерянный проход P1). */
const ENGINE_NOTE = 'предупреждение движка';

function cellTitle(c: Cell, displayName: string, dayOff: string | null): string {
  const parts = [`${displayName}, ${c.date}${dayOff ? `, ${dayOff}` : ''}`];
  parts.push(`По СКУД: ${machineText(c)}`);
  if (c.rvCandidate) parts.push('Работа в выходной или праздник без распоряжения (кандидат РВ)');
  if (c.multiBrigade?.length) {
    parts.push(`Две бригады в один день: также ${brigadeList(c.multiBrigade)}. Проверьте часы.`);
  }
  for (const note of c.engineNotes ?? []) parts.push(engineNote(note));
  if (c.manualHours != null) {
    parts.push(
      `Правка (${c.manualByRole === 'hr' ? 'кадры' : 'бригадир'}): ${hours(c.manualHours)} ч — ${c.manualComment ?? ''}`,
    );
    if (staleEditOf(c)) parts.push(RECALC_TEXT.staleTip);
  }
  if (c.lunch) parts.push('Работал в обед');
  if (c.absenceCode) {
    parts.push(
      `Код отсутствия: ${c.absenceCode}${c.absenceOther ? ' — поставлен в табеле другой бригады' : ''}`,
    );
  }
  if (c.question === 'open') parts.push('Вопрос бригадиру — без ответа');
  if (c.question === 'done') parts.push('Вопрос бригадиру — ответ дан');
  return parts.join('\n');
}

/**
 * Особые состояния-проблемы: красным (дизайн-система, «Цвет»); ошибка расчёта движком — тоже (MVP C3), и дата
 * длиннее суток (A8, Q-26). Смысл цвета продублирован текстом: «н/вых», «>24 ч».
 */
const PROBLEM = new Set<Cell['machineState']>(['no_exit', 'no_entry', 'error', 'over_24h']);

/** Вопросы бригадиру в сетке (003, FR-022–FR-024): числа у дат и у ФИО, переход к списку вопросов. */
export interface GridQuestions {
  /** Вопросов у даты: без ответа (бригадир) или ответов на проверку (кадры). */
  byDate: ReadonlyMap<string, number>;
  /** То же у человека. */
  byPerson: ReadonlyMap<string, number>;
  /** Кадры: числа — ответы на проверку («✓N»); бригадир — вопросы без ответа («?N»). */
  review: boolean;
  onDate: (date: string) => void;
  onPerson: (personId: string) => void;
}

/**
 * Сетка табеля (FR-019): строки по ФИО, дни периода, итог строки, «Итого».
 * Выходные и праздники по производственному календарю — зелёным, в подсказке названы (Q-13); правка — жёлтым; «в обед» —
 * голубым; «нет входа/выхода», «>24 ч» (за дату больше 24 ч, Q-26) и ошибка расчёта — красным; день в двух бригадах —
 * значок и строка в подсказке (Q-15);
 * предупреждения движка за день («потерян проход — проверить») — значок и строки в подсказке;
 * будущие дни пустые. Сегодня — акцентная черта под числом (дизайн-система, «Плотные таблицы»).
 * Табель на движке (MVP C3) присылает часы и итоги готовыми строками формата R5 — они показываются как есть.
 * Карточка дня (003, FR-001, FR-002): любая прошедшая и сегодняшняя ячейка — кнопка, будущие не открываются. Ячейка —
 * 44 × 36 px, на телефоне — 44 × 40 px (цель нажатия, FR-011): сетка на компьютере остаётся плотной.
 * Выбранная ячейка (открыта в правой панели) — кольцо `accent` 2 px; таблица прокручивается к ней.
 * Вопросы бригадиру (003, FR-023): по метке `question` ячейки — уголок `warn` справа сверху у вопроса без ответа,
 * маленькая «✓» `ok` у отвеченного; без заливки и рамки, чтобы не путать с правкой. Проблема дня («нет входа»,
 * «нет выхода», «>24 ч») — красный текст на бледно-красном фоне. Число вопросов — мягкой пилюлей у даты (→ вопросы
 * за дату) и «?N» у ФИО (→ вопросы человека).
 * Путь бригадира (003, US3): ФИО — кнопка карточки сотрудника (FR-034; «Убрать из табеля» — только там, FR-035);
 * `visible` — люди, которых нашёл поиск над табелем или отбор «С вопросами» (FR-040). Пусто — строка с объяснением,
 * что изменить. «Итого за день» и «Итого» — по всему табелю, не по найденным.
 */
export function TimesheetGrid({
  ts,
  onCellClick,
  onName,
  visible,
  selected,
  questions,
}: {
  ts: Timesheet;
  onCellClick: (personId: string, displayName: string, cell: Cell) => void;
  /** Нажатие на ФИО — карточка сотрудника. */
  onName?: (personId: string) => void;
  /** Показать только этих людей; нет — всех. */
  visible?: ReadonlySet<string> | null;
  /** День, открытый в правой панели. */
  selected?: { personId: string; date: string } | null;
  /** Вопросы по табелю; без функции вопросов — нет. */
  questions?: GridQuestions | null;
}) {
  const dayTotals = ts.days.map(
    (_, i) => ts.rows.reduce((acc, r) => acc + Math.round((r.cells[i]?.finalHours ?? 0) * 10), 0) / 10,
  );
  const wrap = useRef<HTMLDivElement>(null);
  const nobody = Boolean(visible) && !ts.rows.some((r) => visible?.has(r.personId));
  const selKey = selected ? cellKey(selected.personId, selected.date) : null;
  // Панель открыта справа поверх листа: справа у таблицы место шириной с панель, чтобы последние дни выводились
  // из-под неё прокруткой.
  const panelOpen = useSidePanelOpen();
  // Таблица прокручивается к выбранной ячейке: сразу и ещё раз, когда панель встанет на место (340 мс).
  useEffect(() => {
    if (!selKey) return;
    const run = () => scrollToCell(wrap.current, selKey);
    run();
    const t = setTimeout(run, 360);
    return () => clearTimeout(t);
  }, [selKey]);
  return (
    <div
      ref={wrap}
      data-panel-room={panelOpen ? 'yes' : undefined}
      className={`overflow-x-auto rounded-card border border-line bg-surface shadow-card ${panelOpen ? SIDE_PANEL_ROOM : ''}`}
    >
      {/*
       * Никого не нашли — строкой над таблицей во всю ширину карточки (прилипает слева при прокрутке вбок): в узкой
       * колонке ФИО на телефоне текст не поместится.
       */}
      {nobody && (
        <div role="status" className="sticky left-0 border-b border-line px-4 py-4 text-[13px]">
          <p className="font-semibold text-ink">Никого не нашли</p>
          <p className="mt-0.5 text-ink-2">Измените поиск по ФИО или № СКУД либо выберите «Все люди».</p>
        </div>
      )}
      <table className="border-separate border-spacing-0 text-[12px]">
        <thead>
          <tr className="text-ink-2">
            <th className="sticky left-0 z-20 min-w-60 border-r border-b border-line bg-subtle px-3 py-2 text-left text-[12.5px] font-semibold text-ink max-sm:min-w-0">
              ФИО
            </th>
            {ts.days.map((d) => {
              const today = d.date === ts.today;
              const n = questions?.byDate.get(d.date) ?? 0;
              const head = (
                <>
                  <div
                    className={`text-[13px] tabular-nums ${today ? 'font-semibold text-accent' : 'text-ink'}`}
                  >
                    {Number(d.date.slice(8, 10))}
                  </div>
                  <div className="text-[11px] font-normal text-ink-3">{weekday(d.date)}</div>
                </>
              );
              return (
                <th
                  key={d.date}
                  title={dayOffLabel(d) ?? undefined}
                  className={`w-11 min-w-11 border-r border-b border-line font-medium ${n ? 'p-0' : 'px-0.5 py-1.5'} ${d.weekend ? 'bg-weekend' : 'bg-subtle'} ${today ? 'shadow-[inset_0_-2px_0_var(--color-accent)]' : ''} ${d.future ? 'opacity-50' : ''}`}
                >
                  {n && questions ? (
                    // Вся шапка даты — кнопка: на телефоне цель нажатия не меньше 40 px.
                    <button
                      type="button"
                      aria-label={`${questions.review ? 'Ответы на проверку' : 'Вопросы'} за ${weekday(d.date)}, ${d.date.slice(8, 10)}.${d.date.slice(5, 7)}: ${n}`}
                      title={`${questions.review ? 'Ответы на проверку' : 'Вопросы без ответа'}: ${n}`}
                      onClick={() => questions.onDate(d.date)}
                      className="flex min-h-10 w-full flex-col items-center px-0.5 py-1 hover:bg-accent-soft"
                    >
                      {head}
                      <span className="mt-0.5 inline-flex h-4 min-w-[18px] items-center justify-center rounded-full bg-warn-soft px-1 text-[10px] font-semibold text-warn tabular-nums">
                        {n}
                      </span>
                    </button>
                  ) : (
                    head
                  )}
                </th>
              );
            })}
            <th className="min-w-18 border-b border-line bg-subtle px-3 text-right text-[12.5px] font-semibold text-ink">
              Итого
            </th>
          </tr>
        </thead>
        <tbody>
          {ts.rows.map((r) =>
            visible && !visible.has(r.personId) ? null : (
              <tr key={r.personId} className="group">
                <td className="sticky left-0 z-10 border-r border-b border-line bg-surface px-3 py-1 whitespace-nowrap group-hover:bg-subtle">
                  {/* На телефоне колонка ФИО уже: дни прокручиваются вбок, ФИО остаётся на месте. */}
                  <div className="flex items-center justify-between gap-2 max-sm:w-[136px]">
                    {onName ? (
                      <button
                        type="button"
                        onClick={() => onName(r.personId)}
                        title={`${r.displayName} — карточка сотрудника`}
                        // Невидимое поле вокруг ФИО — цель нажатия на телефоне не меньше 40 px.
                        className="relative min-w-0 truncate text-left text-[13px] text-ink before:absolute before:-inset-y-2.5 before:-left-3 before:right-0 before:content-[''] hover:text-accent hover:underline"
                      >
                        {r.displayName}
                      </button>
                    ) : (
                      <span className="min-w-0 truncate text-[13px] text-ink" title={r.displayName}>
                        {r.displayName}
                      </span>
                    )}
                    {questions && (questions.byPerson.get(r.personId) ?? 0) > 0 && (
                      <button
                        type="button"
                        onClick={() => questions.onPerson(r.personId)}
                        aria-label={
                          questions.review
                            ? `Ответы на проверку: ${r.displayName}, ${questions.byPerson.get(r.personId)}`
                            : `Вопросы: ${r.displayName}, без ответа ${questions.byPerson.get(r.personId)}`
                        }
                        title={questions.review ? 'Ответы на проверку' : 'Вопросов без ответа'}
                        // Невидимое поле вокруг пилюли — цель нажатия на телефоне.
                        className="relative ml-auto inline-flex h-[18px] shrink-0 items-center rounded-full bg-warn-soft px-1.5 text-[11px] font-semibold text-warn tabular-nums before:absolute before:-inset-x-2 before:-inset-y-3 before:content-[''] hover:bg-warn/15"
                      >
                        {questions.review ? '✓' : '?'}
                        {questions.byPerson.get(r.personId)}
                      </button>
                    )}
                  </div>
                </td>
                {r.cells.map((c, i) => {
                  const day = ts.days[i];
                  const future = day?.future ?? false;
                  let bg = day?.weekend ? 'bg-weekend/70' : '';
                  if (c.lunch) bg = 'bg-lunch';
                  if (c.manualHours != null) bg = 'bg-edit';
                  const flagged = c.finalHours == null && !c.absenceCode && PROBLEM.has(c.machineState);
                  // Проблема дня — красный текст на бледно-красном фоне, не жёлтый, как у правки.
                  if (flagged) bg = 'bg-bad-soft';
                  const mark = c.question ?? null;
                  const key = cellKey(r.personId, c.date);
                  const isSel = key === selKey;
                  return (
                    <td key={c.date} className={`border-r border-b border-line p-0 text-center ${bg}`}>
                      {/* Плотная сетка: 36 px на компьютере, на телефоне — цель нажатия 44 × 40 px (T106). */}
                      {future ? (
                        <span className="block h-9 max-sm:h-10" />
                      ) : (
                        <button
                          type="button"
                          data-cell={key}
                          aria-pressed={isSel}
                          onClick={() => onCellClick(r.personId, r.displayName, c)}
                          title={cellTitle(c, r.displayName, dayOffLabel(day))}
                          className={`relative block h-9 w-full transition-colors max-sm:h-10 ${isSel ? 'bg-accent-soft shadow-[inset_0_0_0_2px_var(--color-accent)]' : 'hover:bg-accent-soft hover:shadow-[inset_0_0_0_1.5px_var(--color-accent)]'}`}
                        >
                          {c.absenceCode ? (
                            <b className="font-semibold text-ink-2">{c.absenceCode}</b>
                          ) : c.finalHours != null ? (
                            <span className="text-[13px] text-ink tabular-nums">
                              {shownHours(c.finalValue, c.finalHours)}
                            </span>
                          ) : (
                            <span
                              className={`text-[11px] leading-tight ${flagged ? 'font-semibold text-bad' : 'text-ink-3 italic'}`}
                            >
                              {STATE_SHORT[c.machineState]}
                            </span>
                          )}
                          {c.manualHours != null && (
                            <span
                              className={`absolute top-0 text-[11px] leading-none text-warn ${mark === 'open' ? 'right-2.5' : 'right-0.5'}`}
                              aria-label="изменено вручную"
                            >
                              ✎{/* «Правка устарела» (004, FR-044): у значка правки — жёлтая точка. */}
                              {staleEditOf(c) && (
                                <span
                                  role="img"
                                  data-mark="stale-edit"
                                  aria-label={RECALC_LABEL.staleEdit}
                                  className="absolute -top-px -left-1 h-1.5 w-1.5 rounded-full bg-warn ring-1 ring-surface"
                                />
                              )}
                            </span>
                          )}
                          {mark === 'open' && (
                            <span
                              role="img"
                              data-mark="open"
                              aria-label="вопрос без ответа"
                              className="absolute top-0 right-0 h-0 w-0 border-t-[8px] border-l-[8px] border-t-warn border-l-transparent"
                            />
                          )}
                          {mark === 'done' && (
                            <span
                              role="img"
                              data-mark="done"
                              aria-label="ответ бригадира"
                              className="absolute right-[3px] bottom-[2px] text-[10px] leading-none font-bold text-ok"
                            >
                              ✓
                            </span>
                          )}
                          {c.multiBrigade?.length ? (
                            <span
                              role="img"
                              className="absolute top-0.5 left-1 text-warn"
                              aria-label={MULTI_BRIGADE}
                            >
                              <Users size={10} strokeWidth={2} aria-hidden />
                            </span>
                          ) : null}
                          {/* Метка вопроса заменяет значок предупреждения: вопрос — о том же дне (003). */}
                          {c.engineNotes?.length && !mark ? (
                            <span
                              role="img"
                              className="absolute bottom-0.5 left-1 text-warn"
                              aria-label={ENGINE_NOTE}
                            >
                              <TriangleAlert size={10} strokeWidth={2} aria-hidden />
                            </span>
                          ) : null}
                        </button>
                      )}
                    </td>
                  );
                })}
                <td className="border-b border-line px-3 text-right text-[13px] font-semibold text-ink tabular-nums group-hover:bg-subtle">
                  {shownHours(r.totalValue, r.total)}
                </td>
              </tr>
            ),
          )}
          <tr className="font-semibold">
            <td className="sticky left-0 z-10 border-r border-line bg-subtle px-3 py-2 text-[12.5px] text-ink">
              Итого за день
            </td>
            {dayTotals.map((v, i) => (
              <td
                key={ts.days[i]?.date}
                className="border-r border-line bg-subtle text-center text-[11px] text-ink-2 tabular-nums"
              >
                {v ? shownHours(ts.days[i]?.totalValue, v) : ''}
              </td>
            ))}
            <td className="bg-subtle px-3 text-right text-[13px] text-ink tabular-nums">
              {shownHours(ts.grandTotalValue, ts.grandTotal)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
